// src/components/shopping/JewelleryProfileModal.tsx
import React, { useState, useEffect } from 'react';
import { 
  X, Loader2, MessageSquare, ShoppingBag, MapPin, 
  Clock, CheckCircle, ArrowLeft, Share2, 
  Users, Heart, Instagram, Twitter, Youtube, Music2, 
  Link as LinkIcon, ArrowRight, 
  Gem, Diamond, Crown, Award as AwardIcon,
  Shield, Sparkles, Info, Layers, Leaf
} from 'lucide-react';
import { useNavigate, useParams } from 'react-router-dom';
import { db } from '../../services/firebase';
import { doc, serverTimestamp, collection, getDoc, getDocs, query, where, updateDoc } from 'firebase/firestore';
import { useAuth } from '../../hooks/useAuth';

// 🟢 CUSTOM SVG ICONS - Matching ScoopStoneApp
const RingIcon = ({ stroke = "#17171A", size = 64, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" stroke={stroke} strokeWidth="2" className={className}>
    <circle cx="50" cy="60" r="26" />
    <path d="M39 36 L50 14 L61 36 L53 42 L47 42 Z" fill={stroke} stroke="none" />
  </svg>
);

const NecklaceIcon = ({ stroke = "#17171A", size = 64, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" stroke={stroke} strokeWidth="2" className={className}>
    <path d="M18 18 Q50 68 82 18" />
    <path d="M43 60 L50 76 L57 60 Z" fill={stroke} stroke="none" />
  </svg>
);

const BraceletIcon = ({ stroke = "#17171A", size = 64, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" stroke={stroke} strokeWidth="2" className={className}>
    <ellipse cx="50" cy="55" rx="32" ry="17" />
    <circle cx="50" cy="38" r="5" fill={stroke} stroke="none" />
  </svg>
);

const WatchIcon = ({ stroke = "#17171A", size = 64, className = "" }) => (
  <svg width={size} height={size} viewBox="0 0 100 100" fill="none" stroke={stroke} strokeWidth="2" className={className}>
    <path d="M40 20 h20 v14 h-20 z" />
    <path d="M40 66 h20 v14 h-20 z" />
    <circle cx="50" cy="50" r="24" />
    <path d="M50 36 v14 l10 8" strokeLinecap="round" />
  </svg>
);

// 🟢 WAX SEAL BADGE
const WaxSeal = ({ size = 40, className = "" }) => (
  <div className={`relative ${className}`} style={{ width: size, height: size }}>
    <svg width={size} height={size} viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="46" fill="#7A1F2B" />
      <circle cx="50" cy="50" r="46" fill="none" stroke="#A6812C" strokeWidth="2" />
      <circle cx="50" cy="50" r="38" fill="none" stroke="#A6812C" strokeWidth="1" opacity="0.6" />
      <text x="50" y="46" textAnchor="middle" fill="#F4E9C9" fontSize="11" fontFamily="Georgia, serif" letterSpacing="1">
        SCOOP
      </text>
      <text x="50" y="60" textAnchor="middle" fill="#F4E9C9" fontSize="8" fontFamily="Georgia, serif" letterSpacing="2">
        VERIFIED
      </text>
    </svg>
  </div>
);

// 🟢 GAVEL ICON
const GavelIcon = ({ size = 14, stroke = "currentColor" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={stroke} strokeWidth="2" strokeLinecap="round">
    <path d="M14 5 L19 10" />
    <path d="M9.5 9.5 L4 15 L6 17 L11.5 11.5" />
    <path d="M13 8 L16 11" />
    <path d="M2 20 h9" />
    <path d="M16 3 L21 8" />
  </svg>
);

// 🟢 AUCTION BADGE
const AuctionBadge = ({ endsAt }: { endsAt: number }) => {
  const [timeLeft, setTimeLeft] = useState(endsAt - Date.now());
  
  useEffect(() => {
    const interval = setInterval(() => {
      setTimeLeft(endsAt - Date.now());
    }, 1000);
    return () => clearInterval(interval);
  }, [endsAt]);
  
  if (timeLeft <= 0) return <span className="text-[#6B6B63] text-[8px] font-['JetBrains_Mono',monospace] font-bold">Ended</span>;
  
  const hours = Math.floor(timeLeft / (1000 * 60 * 60));
  const minutes = Math.floor((timeLeft % (1000 * 60 * 60)) / (1000 * 60));
  
  if (hours > 24) {
    const days = Math.floor(hours / 24);
    return <span className="text-[#C9A96E] text-[8px] font-['JetBrains_Mono',monospace] font-bold">{days}d {hours % 24}h</span>;
  }
  if (hours > 0) {
    return <span className="text-[#C9A96E] text-[8px] font-['JetBrains_Mono',monospace] font-bold">{hours}h {minutes}m</span>;
  }
  return <span className="text-[#7A1F2B] text-[8px] font-['JetBrains_Mono',monospace] font-bold animate-pulse">{minutes}m</span>;
};

// 🟢 LISTING CARD
function ListingCard({ p, onOpen }: any) {
  const getCategoryIcon = () => {
    switch(p.category) {
      case 'Ring': return <RingIcon stroke="#17171A" size={52} />;
      case 'Necklace': return <NecklaceIcon stroke="#17171A" size={52} />;
      case 'Bracelet': return <BraceletIcon stroke="#17171A" size={52} />;
      case 'Watch': return <WatchIcon stroke="#17171A" size={52} />;
      default: return <Gem size={52} className="text-[#6B6B63]" />;
    }
  };

  const isAuction = p.isAuction || false;
  const displayPrice = isAuction ? (p.currentBid || p.startingBid || p.price) : p.price;

  return (
    <div className="bg-white border border-[#E8E0D8] hover:shadow-xl transition-all duration-300 group cursor-pointer hover:-translate-y-1" onClick={() => onOpen?.(p)}>
      <div className="relative aspect-square bg-[#FAF8F3] overflow-hidden flex items-center justify-center">
        {p.image || p.images?.[0] ? (
          <img src={p.image || p.images?.[0]} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" alt={p.name} />
        ) : (
          getCategoryIcon()
        )}
        
        {p.authenticationBadge && (
          <div className="absolute top-3 left-3">
            <WaxSeal size={28} />
          </div>
        )}
        
        {isAuction && p.auctionStatus === 'active' && p.auctionEndDate && (
          <div className="absolute top-3 left-3 flex flex-col gap-1">
            <span className="px-2 py-0.5 bg-[#7A1F2B] text-[#F4E9C9] text-[8px] font-['JetBrains_Mono',monospace] font-bold rounded-full flex items-center gap-1 border border-[#A6812C]">
              <GavelIcon size={10} /> Auction
            </span>
          </div>
        )}
        
        {p.certificate?.isCertified && (
          <span className="absolute top-3 left-3 px-2 py-0.5 bg-[#A6812C] text-white text-[8px] font-['JetBrains_Mono',monospace] font-bold rounded-full flex items-center gap-1">
            <AwardIcon size={10} /> Certified
          </span>
        )}
        
        {isAuction && p.auctionEndDate && (
          <div className="absolute bottom-3 left-3 bg-[#7A1F2B]/90 backdrop-blur-sm px-3 py-1 rounded-full border border-[#A6812C]">
            <div className="flex items-center gap-2 text-[#F4E9C9] text-[9px] font-['JetBrains_Mono',monospace] font-bold">
              <Clock size={12} className="text-[#A6812C]" />
              <AuctionBadge endsAt={p.auctionEndDate} />
            </div>
          </div>
        )}
      </div>
      
      <div className="p-4">
        <div className="flex items-start justify-between gap-2 mb-1">
          <h3 className="font-['Cormorant_Garamond',serif] text-base font-medium text-[#17171A] truncate flex-1">
            {p.name}
          </h3>
          <span className="text-[9px] font-['JetBrains_Mono',monospace] font-bold text-[#A6812C] uppercase tracking-wider whitespace-nowrap">
            {p.condition || 'New'}
          </span>
        </div>
        
        <div className="flex flex-wrap gap-1 mb-2">
          {p.hallmark && (
            <span className="text-[7px] font-['JetBrains_Mono',monospace] font-bold bg-[#A6812C]/10 text-[#A6812C] px-2 py-0.5 rounded-full border border-[#A6812C]/20">
              {p.hallmark}
            </span>
          )}
          {p.metalType && (
            <span className="text-[7px] font-['JetBrains_Mono',monospace] font-bold bg-[#FAF8F3] text-[#6B6B63] px-2 py-0.5 rounded-full border border-[#E8E0D8]">
              {p.metalType}
            </span>
          )}
          {p.gemstone && (
            <span className="text-[7px] font-['JetBrains_Mono',monospace] font-bold bg-purple-100 text-purple-600 px-2 py-0.5 rounded-full border border-purple-200">
              {p.gemstone}
            </span>
          )}
        </div>
        
        <div className="flex items-center justify-between mt-3 pt-3 border-t border-[#E8E0D8]">
          <div>
            {isAuction ? (
              <>
                <span className="text-[8px] font-['JetBrains_Mono',monospace] font-bold text-[#6B6B63] uppercase tracking-wider">Current Bid</span>
                <p className="font-['JetBrains_Mono',monospace] font-bold text-[#A6812C]">R{displayPrice?.toFixed(2)}</p>
                <span className="text-[8px] font-['JetBrains_Mono',monospace] text-[#6B6B63]">{p.numberOfBids || 0} bids</span>
              </>
            ) : (
              <>
                <span className="text-[8px] font-['JetBrains_Mono',monospace] font-bold text-[#6B6B63] uppercase tracking-wider">Price</span>
                <p className="font-['JetBrains_Mono',monospace] font-bold text-[#A6812C]">R{displayPrice?.toFixed(2)}</p>
              </>
            )}
          </div>
          <span className="text-[10px] font-['JetBrains_Mono',monospace] text-[#6B6B63] group-hover:text-[#A6812C] transition-colors">
            {isAuction ? "Place a bid →" : "View piece →"}
          </span>
        </div>
      </div>
    </div>
  );
}

// ============================================================================
// JEWELLERY PROFILE PAGE - Matching ScoopStoneApp Style
// ============================================================================
export const JewelleryProfilePage = ({ 
  brand: initialBrand, 
  products: initialProducts = [], 
  onClose,
  onSelectProduct,
  onSendMessage
}: any) => {
  const navigate = useNavigate();
  const { jewelleryId: paramJewelleryId } = useParams<{ jewelleryId: string }>();
  const { user: currentUser, profile: userProfile } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showShareMenu, setShowShareMenu] = useState(false);
  const [brand, setBrand] = useState<any>(initialBrand || null);
  const [products, setProducts] = useState<any[]>(initialProducts || []);
  const [loading, setLoading] = useState(!initialBrand);
  const [soldCount, setSoldCount] = useState(0);
  const [lastActive, setLastActive] = useState<string | null>(null);

  // 🟢 PRODUCT CLICK HANDLER
  const handleProductClick = (product: any) => {
    if (onSelectProduct) {
      onSelectProduct(product);
    } else {
      console.warn('⚠️ onSelectProduct not provided - product click will not work');
    }
  };

  // 🟢 MESSAGE HANDLER
  const handleSendMessage = () => {
    if (!currentUser) {
      window.dispatchEvent(new CustomEvent('open-login-modal'));
      return;
    }
    
    const sellerId = brand?.id || brand?.uid || brand?.workspaceId;
    
    if (!sellerId) {
      alert("Unable to message this jeweller.");
      return;
    }
    
    setIsSubmitting(true);
    
    if (onSendMessage) {
      onSendMessage(sellerId, brand?.businessName || brand?.displayName || 'Jewellery Studio');
      setIsSubmitting(false);
      return;
    }
    
    try {
      window.dispatchEvent(new CustomEvent('open-messages', {
        detail: { userId: sellerId }
      }));
    } catch (e) {
      console.error(e);
      alert("Failed to open chat.");
    } finally {
      setIsSubmitting(false);
    }
  };

  // ============================================================================
  // Fetch Data
  // ============================================================================
  useEffect(() => {
    const fetchJewelleryData = async () => {
      const jewelleryId = initialBrand?.id || initialBrand?.uid || paramJewelleryId;
      
      if (!jewelleryId) {
        return;
      }

      try {
        setLoading(true);
        
        let brandData = initialBrand;
        if (!brandData) {
          const userDoc = await getDoc(doc(db, 'users', jewelleryId));
          if (userDoc.exists()) {
            const userData = userDoc.data();
            
            try {
              await updateDoc(doc(db, 'users', jewelleryId), {
                lastActive: serverTimestamp()
              });
            } catch (e) {
              console.error('Error updating last active:', e);
            }
            
            let sold = 0;
            try {
              const productQuery = query(
                collection(db, 'products'),
                where('workspaceId', '==', jewelleryId),
                where('status', '==', 'sold')
              );
              const productSnap = await getDocs(productQuery);
              sold = productSnap.size;
            } catch (e) {
              console.error('Error fetching sold count:', e);
            }
            setSoldCount(sold);
            
            if (userData.lastActive) {
              const lastActiveDate = userData.lastActive.toDate ? userData.lastActive.toDate() : new Date(userData.lastActive);
              const now = new Date();
              const diff = Math.floor((now.getTime() - lastActiveDate.getTime()) / 1000);
              if (diff < 60) {
                setLastActive('Just now');
              } else if (diff < 3600) {
                setLastActive(`${Math.floor(diff / 60)}m ago`);
              } else if (diff < 86400) {
                setLastActive(`${Math.floor(diff / 3600)}h ago`);
              } else if (diff < 604800) {
                setLastActive(`${Math.floor(diff / 86400)}d ago`);
              } else {
                setLastActive(lastActiveDate.toLocaleDateString());
              }
            } else {
              setLastActive('Recently');
            }
            
            brandData = {
              id: userDoc.id,
              uid: userDoc.id,
              ...userData,
              businessName: userData.businessName || userData.displayName || userData.username || 'Jewellery Studio',
              displayName: userData.displayName || userData.businessName || userData.username || 'Jewellery Studio',
              location: userData.location || userData.sellerLocation || 'Global',
              photoURL: userData.photoURL || userData.logoUrl || null,
              logoUrl: userData.logoUrl || userData.photoURL || null,
              bannerUrl: userData.bannerUrl || null,
              bio: userData.description || userData.bio || '',
              description: userData.description || userData.bio || '',
              verified: userData.verified || false,
              followersCount: userData.followersCount || userData.followers || 0,
              showAuthenticationBadge: userData.showAuthenticationBadge !== undefined ? userData.showAuthenticationBadge : true,
              showHallmark: userData.showHallmark !== undefined ? userData.showHallmark : true,
              showCertification: userData.showCertification !== undefined ? userData.showCertification : true,
              featuredCollection: userData.featuredCollection || 'Everyday Essentials',
              curatorsNote: userData.curatorsNote || '',
              layout: userData.layout || 'grid',
              primaryColor: userData.primaryColor || '#C9A96E',
              province: userData.province || 'Gauteng',
              instagramHandle: userData.instagramHandle || '',
              tiktokHandle: userData.tiktokHandle || '',
              twitterHandle: userData.twitterHandle || '',
              facebookHandle: userData.facebookHandle || '',
              youtubeHandle: userData.youtubeHandle || '',
              footerText: userData.footerText || '',
              jewelleryType: userData.jewelleryType || 'fine',
              hallmark: userData.hallmark || '925',
              hallmarkStamp: userData.hallmarkStamp || '925',
              materials: userData.materials || ['Gold', 'Silver'],
              sustainability: userData.sustainability || 'Ethically Sourced',
              certifications: userData.certifications || [],
              customOrdersEnabled: userData.customOrdersEnabled || false,
              turnAroundDays: userData.turnAroundDays || 14,
              metalTypes: userData.metalTypes || ['Yellow Gold', 'White Gold', 'Rose Gold'],
              gemstones: userData.gemstones || ['Diamond', 'Sapphire', 'Ruby'],
              authenticationLevel: userData.authenticationLevel || 'premium',
            };
            setBrand(brandData);
          } else {
            return;
          }
        } else {
          setBrand(brandData);
        }

        if (brandData) {
          const jewelleryIdToUse = brandData.id || brandData.uid || jewelleryId;
          
          let productsQuery = query(
            collection(db, 'products'),
            where('workspaceId', '==', jewelleryIdToUse)
          );
          let productsSnap = await getDocs(productsQuery);
          
          if (productsSnap.empty) {
            productsQuery = query(
              collection(db, 'products'),
              where('sellerId', '==', jewelleryIdToUse)
            );
            productsSnap = await getDocs(productsQuery);
          }
          
          if (productsSnap.empty) {
            productsQuery = query(
              collection(db, 'products'),
              where('businessId', '==', jewelleryIdToUse)
            );
            productsSnap = await getDocs(productsQuery);
          }

          if (productsSnap.empty) {
            productsQuery = query(
              collection(db, 'products'),
              where('workspaceType', '==', 'jewellery')
            );
            productsSnap = await getDocs(productsQuery);
            
            const allProducts = productsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
            const filteredProducts = allProducts.filter((p: any) => 
              p.sellerId === jewelleryIdToUse || 
              p.workspaceId === jewelleryIdToUse || 
              p.businessId === jewelleryIdToUse ||
              p.sellerId === brandData.id ||
              p.workspaceId === brandData.id
            );
            setProducts(filteredProducts);
            
            const soldItems = filteredProducts.filter((p: any) => p.status === 'sold' || p.stock === 0);
            setSoldCount(soldItems.length);
            return;
          }

          const productList = productsSnap.docs.map(d => ({ id: d.id, ...d.data() }));
          setProducts(productList);
          
          const soldItems = productList.filter((p: any) => p.status === 'sold' || p.stock === 0);
          setSoldCount(soldItems.length);
        }
      } catch (error) {
        console.error('Error fetching jewellery data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchJewelleryData();
  }, [initialBrand, paramJewelleryId]);

  const handleClose = () => {
    if (onClose) {
      onClose();
    } else {
      navigate(-1);
    }
  };

  const handleCopyLink = () => {
    const shareUrl = `${window.location.origin}/jewellery/${brand?.id || brand?.uid || ''}`;
    navigator.clipboard.writeText(shareUrl);
    setShowShareMenu(false);
    alert("Link copied to clipboard!");
  };

  const handleNativeShare = async () => {
    const shareUrl = `${window.location.origin}/jewellery/${brand?.id || brand?.uid || ''}`;
    if (navigator.share) {
      try {
        await navigator.share({
          title: brand?.businessName || 'Jewellery Studio',
          text: `Discover ${brand?.businessName || 'Jewellery Studio'} on Scoop & Stone! ✨`,
          url: shareUrl
        });
        setShowShareMenu(false);
      } catch (err) {
        console.error("Share failed:", err);
      }
    } else {
      handleCopyLink();
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-[#FDFCFA]">
        <Loader2 className="animate-spin text-[#A6812C]" size={40} />
      </div>
    );
  }

  if (!brand) return null;

  const brandName = brand.businessName || 
                   brand.displayName || 
                   brand.rootName || 
                   brand.name || 
                   'Jewellery Studio';
  
  const brandImage = brand.photoURL || 
                    brand.logoUrl || 
                    brand.logo || 
                    brand.avatar || 
                    null;
  
  const bannerImage = brand.bannerUrl || '';
  
  const location = brand.location || 
                  brand.sellerLocation || 
                  brand.userLocation || 
                  brand.address?.city || 
                  'Global';
  
  const province = brand.province || '';
  const fullLocation = location + (province ? `, ${province}` : '');
  
  const bio = brand.description || brand.bio || '';
  
  const followersCount = brand.followersCount || brand.followers || 0;
  
  const showAuthenticationBadge = brand.showAuthenticationBadge !== undefined ? brand.showAuthenticationBadge : true;
  const showHallmark = brand.showHallmark !== undefined ? brand.showHallmark : true;
  const showCertification = brand.showCertification !== undefined ? brand.showCertification : true;
  const curatorsNote = brand.curatorsNote || '';
  
  const hallmark = brand.hallmark || '925';
  const jewelleryType = brand.jewelleryType || 'fine';
  const materials = brand.materials || ['Gold', 'Silver'];
  const sustainability = brand.sustainability || 'Ethically Sourced';
  const certifications = brand.certifications || [];
  const customOrdersEnabled = brand.customOrdersEnabled || false;
  const turnAroundDays = brand.turnAroundDays || 14;
  const authenticationLevel = brand.authenticationLevel || 'premium';

  return (
    <div className="min-h-screen w-full bg-[#FDFCFA] text-[#17171A] font-['Inter',sans-serif] overflow-y-auto">
      
      {/* Breadcrumb Navigation - Sticky but scrollable */}
      <div className="sticky top-0 z-10 bg-white/95 backdrop-blur-xl border-b border-[#E8E0D8] px-4 sm:px-6 md:px-8 py-4 flex justify-between items-center">
        <button 
          onClick={handleClose} 
          className="flex items-center gap-2 text-xs font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-[#6B6B63] hover:text-[#17171A] transition-colors"
        >
          <ArrowLeft size={16} />
          <span className="hidden sm:inline">Back to Shop</span>
        </button>
        
        <h2 className="text-[10px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-[#6B6B63] truncate max-w-[150px] sm:max-w-xs">
          {brandName}
        </h2>
        
        <button 
          onClick={() => setShowShareMenu(true)}
          className="p-2 text-[#6B6B63] hover:text-[#17171A] transition-colors rounded-full hover:bg-[#FAF8F3]"
        >
          <Share2 size={18} />
        </button>
      </div>

      {/* Profile Content - Scrollable */}
      <div className="max-w-6xl mx-auto px-4 sm:px-6 md:px-8 py-8">
        
        {/* Banner */}
        {bannerImage && (
          <div className="relative w-full h-48 sm:h-64 rounded-2xl overflow-hidden mb-8 shadow-xl border border-[#E8E0D8]">
            <img src={bannerImage} className="w-full h-full object-cover" alt="Banner" />
            <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent" />
            <div className="absolute bottom-6 left-6 flex items-center gap-3">
              <Diamond size={20} className="text-[#A6812C]" />
              <span className="text-white/80 text-[10px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest">
                {jewelleryType === 'fine' ? 'Fine Jewellery' : 'Fashion Jewellery'}
              </span>
            </div>
          </div>
        )}

        {/* Logo + Name Section */}
        <div className="flex flex-col items-center text-center">
          <div className="relative w-28 h-28 rounded-full border-4 border-[#E8E0D8] overflow-hidden shadow-xl flex items-center justify-center mb-4 bg-white">
            {brandImage ? (
              <img src={brandImage} className="w-full h-full object-cover" alt={brandName} />
            ) : (
              <Gem size={40} className="text-[#A6812C]" />
            )}
            <div className="absolute inset-0 rounded-full border border-[#C9A96E]/20 animate-pulse" />
          </div>
          
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-['Cormorant_Garamond',serif] font-medium tracking-tight text-[#17171A]">
            {brandName}
          </h1>
          
          {/* Metadata */}
          <div className="flex flex-wrap items-center justify-center gap-3 mt-3">
            <div className="flex items-center gap-1.5 text-[10px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-[#6B6B63]">
              <MapPin size={12} /> {fullLocation}
            </div>
            {showHallmark && hallmark && (
              <span className="px-3 py-1 border border-[#E8E0D8] text-[9px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-[#6B6B63] bg-white">
                {hallmark}
              </span>
            )}
            {brand.verified && (
              <div className="flex items-center gap-1">
                <CheckCircle size={14} className="text-[#A6812C]" />
                <span className="text-[9px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-[#A6812C]">Verified</span>
              </div>
            )}
          </div>

          {/* Stats */}
          <div className="grid grid-cols-4 gap-3 mt-5 max-w-sm mx-auto w-full">
            <div className="p-3 border-t border-b border-r border-l-0 first:border-l last:border-r border-[#E8E0D8] bg-white">
              <span className="text-[8px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest block text-[#6B6B63]">Pieces</span>
              <p className="font-['Cormorant_Garamond',serif] text-lg font-medium text-[#17171A]">{products.length}</p>
            </div>
            <div className="p-3 border-t border-b border-[#E8E0D8] bg-white">
              <span className="text-[8px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest block text-[#6B6B63]">Sold</span>
              <p className="font-['Cormorant_Garamond',serif] text-lg font-medium text-[#17171A]">{soldCount}</p>
            </div>
            <div className="p-3 border-t border-b border-[#E8E0D8] bg-white">
              <span className="text-[8px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest block text-[#6B6B63]">Certified</span>
              <p className="font-['Cormorant_Garamond',serif] text-lg font-medium text-[#17171A]">
                {products.filter((p: any) => p.certificate?.isCertified).length}
              </p>
            </div>
            <div className="p-3 border-t border-b border-l border-r-0 first:border-l last:border-r border-[#E8E0D8] bg-white">
              <span className="text-[8px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest block text-[#6B6B63]">Active</span>
              <p className="font-['Cormorant_Garamond',serif] text-lg font-medium text-[#17171A]">{lastActive || 'Recently'}</p>
            </div>
          </div>

          {/* Jewellery Badges */}
          <div className="flex flex-wrap justify-center gap-2 mt-4">
            {jewelleryType && (
              <span className="px-4 py-1.5 border border-[#E8E0D8] text-[9px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-[#6B6B63] bg-white">
                <Diamond size={10} className="inline mr-1.5" /> {jewelleryType === 'fine' ? 'Fine Jewellery' : 'Fashion Jewellery'}
              </span>
            )}
            {materials && materials.length > 0 && (
              <span className="px-4 py-1.5 border border-[#E8E0D8] text-[9px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-[#6B6B63] bg-white">
                {materials.join(' • ')}
              </span>
            )}
            {sustainability && (
              <span className="px-4 py-1.5 border border-[#E8E0D8] text-[9px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-[#6B6B63] bg-white">
                <Leaf size={10} className="inline mr-1.5" /> {sustainability}
              </span>
            )}
            {authenticationLevel && (
              <span className="px-4 py-1.5 border border-[#A6812C] text-[9px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-[#A6812C] bg-white">
                <Shield size={10} className="inline mr-1.5" /> {authenticationLevel.charAt(0).toUpperCase() + authenticationLevel.slice(1)} Authentication
              </span>
            )}
          </div>

          {/* Curator's Note */}
          {curatorsNote && (
            <div className="mt-4 p-5 bg-white border border-[#E8E0D8] max-w-xl mx-auto">
              <p className="text-xs italic leading-relaxed text-[#6B6B63]">
                "{curatorsNote}"
              </p>
            </div>
          )}

          {/* Bio */}
          {bio && (
            <p className="text-xs mt-3 max-w-xl mx-auto leading-relaxed text-[#6B6B63]">
              {bio}
            </p>
          )}

          {/* Social Icons */}
          <div className="flex flex-wrap justify-center gap-3 mt-4">
            {brand.instagramHandle && (
              <a href={`https://instagram.com/${brand.instagramHandle}`} target="_blank" rel="noreferrer" className="p-2 border border-[#E8E0D8] rounded-lg hover:bg-[#FAF8F3] transition-colors text-[#6B6B63] hover:text-[#17171A]">
                <Instagram size={16} />
              </a>
            )}
            {brand.tiktokHandle && (
              <a href={`https://tiktok.com/@${brand.tiktokHandle}`} target="_blank" rel="noreferrer" className="p-2 border border-[#E8E0D8] rounded-lg hover:bg-[#FAF8F3] transition-colors text-[#6B6B63] hover:text-[#17171A]">
                <Music2 size={16} />
              </a>
            )}
            {brand.twitterHandle && (
              <a href={`https://twitter.com/${brand.twitterHandle}`} target="_blank" rel="noreferrer" className="p-2 border border-[#E8E0D8] rounded-lg hover:bg-[#FAF8F3] transition-colors text-[#6B6B63] hover:text-[#17171A]">
                <Twitter size={16} />
              </a>
            )}
          </div>

          {/* Message Button */}
          <div className="flex gap-3 w-full max-w-md mt-5">
            <button 
              onClick={handleSendMessage}
              disabled={isSubmitting}
              className="flex-1 py-4 font-['JetBrains_Mono',monospace] font-bold tracking-widest text-[10px] uppercase transition-all border-2 border-[#17171A] bg-[#17171A] text-white hover:bg-[#2A2A2A] disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isSubmitting ? <Loader2 size={14} className="animate-spin" /> : <MessageSquare size={14} />} 
              Message 
            </button>
          </div>
        </div>

        {/* Section Divider */}
        <div className="border-t border-[#E8E0D8] mt-8 pt-8">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-['Cormorant_Garamond',serif] text-2xl font-medium text-[#17171A]">
              Currently selling
            </h2>
            <span className="text-[10px] font-['JetBrains_Mono',monospace] font-bold text-[#6B6B63] uppercase tracking-widest">
              {products.length} piece{products.length === 1 ? '' : 's'}
            </span>
          </div>
          
          {products.length === 0 ? (
            <div className="text-center py-16 bg-white border border-[#E8E0D8]">
              <Gem className="mx-auto mb-3 text-[#6B6B63]" size={32} />
              <p className="text-[10px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-[#6B6B63]">
                No pieces available
              </p>
            </div>
          ) : (
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 lg:gap-6">
              {products.map((p: any) => (
                <ListingCard key={p.id} p={p} onOpen={handleProductClick} />
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Share Menu Modal */}
      {showShareMenu && (
        <div className="fixed inset-0 z-[120] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setShowShareMenu(false)}>
          <div className="bg-white border border-[#E8E0D8] p-8 w-full max-w-sm text-center shadow-2xl" onClick={e => e.stopPropagation()}>
            <div className="w-16 h-16 border border-[#E8E0D8] flex items-center justify-center mx-auto mb-4">
              <Share2 size={24} className="text-[#A6812C]" />
            </div>
            <h3 className="font-['Cormorant_Garamond',serif] text-xl font-medium text-[#17171A]">Share Studio</h3>
            <p className="text-[9px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-[#6B6B63] mt-1">
              Share this jewellery studio
            </p>
            <div className="space-y-3 mt-6">
              <button onClick={handleNativeShare} className="w-full py-3 bg-[#17171A] text-white font-['JetBrains_Mono',monospace] text-[10px] uppercase tracking-widest hover:bg-[#2A2A2A] transition-all flex items-center justify-center gap-2">
                <Users size={14} /> Share
              </button>
              <button onClick={handleCopyLink} className="w-full py-3 border border-[#E8E0D8] text-[#17171A] font-['JetBrains_Mono',monospace] text-[10px] uppercase tracking-widest hover:bg-[#FAF8F3] transition-all flex items-center justify-center gap-2">
                <LinkIcon size={14} /> Copy Link
              </button>
            </div>
            <button onClick={() => setShowShareMenu(false)} className="mt-4 text-[9px] font-['JetBrains_Mono',monospace] font-bold uppercase tracking-widest text-[#6B6B63] hover:text-[#17171A] transition-colors">
              Close
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================================
// MODAL WRAPPER - FULL SCREEN WITH SCROLLING
// ============================================================================
export const JewelleryProfileModal = ({ 
  isOpen, 
  onClose, 
  brand, 
  products = [], 
  onSelectProduct,
  onSendMessage 
}: any) => {
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-[50] w-full h-full bg-[#FDFCFA] overflow-y-auto">
      <JewelleryProfilePage 
        brand={brand} 
        products={products} 
        onClose={onClose}
        onSelectProduct={onSelectProduct}
        onSendMessage={onSendMessage}
      />
    </div>
  );
};

export default JewelleryProfileModal;